import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Hash, User, ExternalLink, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import Button from "../common/Button";
import { bindTelegramAccountDirect } from "../../services/telegramBindingService";

const TelegramDirectBindModal = ({ isOpen, onClose, onSuccess, currentChatId = "", currentUsername = "" }) => {
  const { t } = useTranslation();
  const [chatId, setChatId] = useState(currentChatId);
  const [username, setUsername] = useState(currentUsername);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanChatId = String(chatId || "").trim();
    if (!cleanChatId) {
      toast.error(t("profile.telegram_chat_id_required", "Please enter your Telegram Chat ID"));
      return;
    }

    setLoading(true);
    try {
      const cleanUsername = String(username || "").trim().replace(/^@+/, "");
      const result = await bindTelegramAccountDirect({
        chatId: cleanChatId,
        username: cleanUsername ? `@${cleanUsername}` : "",
      });

      toast.success(t("profile.telegram_direct_bind_success", "Telegram account linked successfully!"));
      if (onSuccess) {
        onSuccess(result || { chatId: cleanChatId, telegramUsername: cleanUsername });
      }
      onClose();
    } catch (err) {
      console.error("Direct bind error:", err);
      toast.error(err?.message || t("profile.telegram_direct_bind_error", "Failed to link Telegram account"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            className="relative w-full max-w-md rounded-3xl border border-gray-100 bg-white p-6 shadow-2xl dark:border-gray-800 dark:bg-gray-900"
          >
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-300">
                  <Send className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-gray-900 dark:text-white">
                    {t("profile.telegram_manual_link_title", "Link Telegram Manually")}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("profile.telegram_manual_link_subtitle", "Directly bind using your Telegram Chat ID")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1.5">
                  {t("profile.telegram_chat_id_label", "Telegram Chat ID")} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                    <Hash className="h-4 w-4" />
                  </div>
                  <input
                    type="text"
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    placeholder="e.g. 123456789"
                    required
                    className="block w-full rounded-2xl border border-gray-200 bg-gray-50/50 py-2.5 pl-10 pr-3 text-sm text-gray-900 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:bg-gray-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1.5">
                  {t("profile.telegram_username_label", "Telegram Username (Optional)")}
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                    <User className="h-4 w-4" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. your_telegram_handle"
                    className="block w-full rounded-2xl border border-gray-200 bg-gray-50/50 py-2.5 pl-10 pr-3 text-sm text-gray-900 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:bg-gray-900"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-sky-100 bg-sky-50/70 p-3.5 text-xs text-sky-800 dark:border-sky-900/40 dark:bg-sky-950/20 dark:text-sky-300">
                <p className="font-bold flex items-center gap-1.5 mb-1">
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t("profile.how_to_find_chat_id", "How to get your Telegram Chat ID:")}
                </p>
                <ol className="list-decimal list-inside space-y-1 text-[11px] leading-relaxed opacity-90">
                  <li>Open Telegram and search for <code>@userinfobot</code> or <code>@raw_data_bot</code>.</li>
                  <li>Press <b>Start</b>. The bot will reply with your numeric <b>Id</b>.</li>
                  <li>Copy and paste that numeric ID into the field above and tap <b>Link Account</b>.</li>
                </ol>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                  className="rounded-2xl"
                >
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  loading={loading}
                  icon={CheckCircle2}
                  className="rounded-2xl bg-sky-600 hover:bg-sky-700"
                >
                  {t("profile.link_account_submit", "Link Account")}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TelegramDirectBindModal;
